import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import Select from '../Common/Select';
import Textarea from '../Common/Textarea';
import api from '../../utils/api';
import { showSuccess, showError, showDeleteConfirm } from '../../utils/swal';
import { Trash2, Save, X, Search } from 'lucide-react';

// Helper: tercih verilerinden filtre URL'i oluştur
const buildPreferenceFilterURL = (pref) => {
  const params = new URLSearchParams();
  if (pref.property_type_id) params.set('property_type_id', pref.property_type_id);
  if (pref.listing_type_id) params.set('listing_type_id', pref.listing_type_id);
  if (pref.room_definition_id) params.set('room_definition_id', pref.room_definition_id);
  if (pref.facade_type_id) params.set('facade_type_id', pref.facade_type_id);
  if (pref.city_id) params.set('city_id', pref.city_id);
  if (pref.district_id) params.set('district_id', pref.district_id);
  if (pref.neighborhood_id) params.set('neighborhood_id', pref.neighborhood_id);
  if (pref.min_floor) params.set('min_floor', pref.min_floor);
  if (pref.max_floor) params.set('max_floor', pref.max_floor);
  if (pref.min_sqm) params.set('min_sqm', pref.min_sqm);
  if (pref.max_sqm) params.set('max_sqm', pref.max_sqm);
  if (pref.min_price) params.set('min_price', pref.min_price);
  if (pref.max_price) params.set('max_price', pref.max_price);
  if (pref.min_building_age) params.set('min_building_age', pref.min_building_age);
  if (pref.max_building_age) params.set('max_building_age', pref.max_building_age);
  return `/portfolios?${params.toString()}`;
};

// Helper: ilişki üzerinden isim al
const getRelationName = (relation) => relation?.name || '-';

const MeetingPreference = ({ PropertyPreferencesId }) => {
  const navigate = useNavigate();
  const [preference, setPreference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Dinamik opsiyonlar
  const [options, setOptions] = useState({
    property_types: [],
    residential_types: [],
    listing_types: [],
    room_definitions: [],
    facade_types: [],
    cities: [],
  });
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const [formData, setFormData] = useState({
    property_type_id: '',
    listing_type_id: '',
    residential_type_id: '',
    room_definition_id: '',
    facade_type_id: '',
    city_id: '',
    district_id: '',
    neighborhood_id: '',
    min_floor: '',
    max_floor: '',
    has_elevator: false,
    min_sqm: '',
    max_sqm: '',
    min_price: '',
    max_price: '',
    min_building_age: '',
    max_building_age: '',
    additional_notes: '',
  });

  useEffect(() => {
    fetchOptions();
    if (PropertyPreferencesId) {
      fetchPreferenceById();
    } else {
      setLoading(false);
    }
  }, [PropertyPreferencesId]);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/portfolio-options');
      setOptions(response.data);
    } catch (error) {
      console.error('Opsiyonlar yüklenemedi:', error);
    }
  };

  const fetchPreferenceById = async () => {
    try {
      const response = await api.get(`/customer-property-preferences/${PropertyPreferencesId}`);
      setPreference(response.data);

      // Konum verilerini yükle
      if (response.data.city_id) fetchDistricts(response.data.city_id);
      if (response.data.district_id) fetchNeighborhoods(response.data.district_id);

      setLoading(false);
    } catch (error) {
      console.error('Tercih yüklenemedi:', error);
      setLoading(false);
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await api.get('/districts', { params: { city_id: cityId } });
      setDistricts(response.data);
    } catch (error) { console.error('İlçeler yüklenemedi:', error); }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      const response = await api.get('/neighborhoods', { params: { district_id: districtId } });
      setNeighborhoods(response.data);
    } catch (error) { console.error('Mahalleler yüklenemedi:', error); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'city_id') {
      setFormData(prev => ({ ...prev, city_id: value, district_id: '', neighborhood_id: '' }));
      if (value) fetchDistricts(value);
      else { setDistricts([]); setNeighborhoods([]); }
      return;
    }
    if (name === 'district_id') {
      setFormData(prev => ({ ...prev, district_id: value, neighborhood_id: '' }));
      if (value) fetchNeighborhoods(value);
      else setNeighborhoods([]);
      return;
    }

    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/customer-property-preferences/${editingId}`, formData);
        showSuccess('Tercih başarıyla güncellendi');
      } else {
        await api.post('/customer-property-preferences', formData);
        showSuccess('Tercih başarıyla eklendi');
      }

      fetchPreferenceById();
      resetForm();
    } catch (error) {
      console.error('Tercih kaydedilemedi:', error);
      showError('Tercih kaydedilirken bir hata oluştu');
    }
  };

  const handleEdit = (pref) => {
    setFormData({
      property_type_id: pref.property_type_id || '',
      listing_type_id: pref.listing_type_id || '',
      residential_type_id: pref.residential_type_id || '',
      room_definition_id: pref.room_definition_id || '',
      facade_type_id: pref.facade_type_id || '',
      city_id: pref.city_id || '',
      district_id: pref.district_id || '',
      neighborhood_id: pref.neighborhood_id || '',
      min_floor: pref.min_floor || '',
      max_floor: pref.max_floor || '',
      has_elevator: pref.has_elevator || false,
      min_sqm: pref.min_sqm || '',
      max_sqm: pref.max_sqm || '',
      min_price: pref.min_price || '',
      max_price: pref.max_price || '',
      min_building_age: pref.min_building_age || '',
      max_building_age: pref.max_building_age || '',
      additional_notes: pref.additional_notes || '',
    });
    setEditingId(pref.id);
    setShowForm(true);

    // Konum verilerini yükle
    if (pref.city_id) fetchDistricts(pref.city_id);
    if (pref.district_id) fetchNeighborhoods(pref.district_id);
  };

  const handleDelete = async (id) => {
    const result = await showDeleteConfirm('Bu tercihi');
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/customer-property-preferences/${id}`);
      showSuccess('Tercih başarıyla silindi');
      fetchPreferenceById();
    } catch (error) {
      console.error('Tercih silinemedi:', error);
      showError('Tercih silinirken bir hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      property_type_id: '',
      listing_type_id: '',
      residential_type_id: '',
      room_definition_id: '',
      facade_type_id: '',
      city_id: '',
      district_id: '',
      neighborhood_id: '',
      min_floor: '',
      max_floor: '',
      has_elevator: false,
      min_sqm: '',
      max_sqm: '',
      min_price: '',
      max_price: '',
      min_building_age: '',
      max_building_age: '',
      additional_notes: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const selectedPropertyType = options.property_types.find(t => t.id == formData.property_type_id);

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-4">
      {showForm && (
        <Card title={editingId ? 'Tercih Düzenle' : 'Yeni Tercih Ekle'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Emlak Tipi" name="property_type_id" value={formData.property_type_id} onChange={handleChange}
                options={options.property_types.map(t => ({ value: t.id, label: t.name }))} />
              <Select label="İlan Tipi" name="listing_type_id" value={formData.listing_type_id} onChange={handleChange}
                options={options.listing_types.map(t => ({ value: t.id, label: t.name }))} />
              {selectedPropertyType?.code === 'residential' && (
                <Select label="Konut Tipi" name="residential_type_id" value={formData.residential_type_id} onChange={handleChange}
                  options={options.residential_types.map(t => ({ value: t.id, label: t.name }))} />
              )}
              <Select label="Oda Sayısı" name="room_definition_id" value={formData.room_definition_id} onChange={handleChange}
                options={options.room_definitions.map(t => ({ value: t.id, label: t.name }))} />
              <Select label="Cephe" name="facade_type_id" value={formData.facade_type_id} onChange={handleChange}
                options={options.facade_types.map(t => ({ value: t.id, label: t.name }))} />
              <Select label="İl" name="city_id" value={formData.city_id} onChange={handleChange}
                options={options.cities.map(c => ({ value: c.id, label: c.name }))} />
              <Select label="İlçe" name="district_id" value={formData.district_id} onChange={handleChange}
                options={districts.map(d => ({ value: d.id, label: d.name }))} />
              <Select label="Mahalle" name="neighborhood_id" value={formData.neighborhood_id} onChange={handleChange}
                options={neighborhoods.map(n => ({ value: n.id, label: n.name }))} />

              <div className="grid grid-cols-2 gap-4">
                <Input label="Min Kat" name="min_floor" type="number" value={formData.min_floor} onChange={handleChange} />
                <Input label="Max Kat" name="max_floor" type="number" value={formData.max_floor} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Min m²" name="min_sqm" type="number" value={formData.min_sqm} onChange={handleChange} />
                <Input label="Max m²" name="max_sqm" type="number" value={formData.max_sqm} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Min Fiyat (₺)" name="min_price" type="number" value={formData.min_price} onChange={handleChange} />
                <Input label="Max Fiyat (₺)" name="max_price" type="number" value={formData.max_price} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Min Bina Yaşı" name="min_building_age" type="number" value={formData.min_building_age} onChange={handleChange} />
                <Input label="Max Bina Yaşı" name="max_building_age" type="number" value={formData.max_building_age} onChange={handleChange} />
              </div>
              <div className="flex items-center h-full pt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" name="has_elevator" checked={formData.has_elevator} onChange={handleChange}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                  <span className="text-sm font-medium text-gray-700">Asansör Var</span>
                </label>
              </div>
            </div>

            <Textarea label="Ek Notlar" name="additional_notes" value={formData.additional_notes} onChange={handleChange} rows={3} />

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={resetForm}>
                <X size={16} /> İptal
              </Button>
              <Button type="submit">
                <Save size={16} /> {editingId ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="space-y-4">
        {!preference ? (
          <Card>
            <p className="text-center text-gray-500 py-8">Henüz tercih eklenmemiş</p>
          </Card>
        ) : (
          <Card key={preference.id}>
            <div className="flex justify-between items-start">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Emlak Tipi</p>
                  <p className="font-semibold">{getRelationName(preference.property_type)}</p>
                </div>
                {preference.listing_type && (
                  <div>
                    <p className="text-sm text-gray-500">İlan Tipi</p>
                    <p className="font-semibold">{getRelationName(preference.listing_type)}</p>
                  </div>
                )}
                {preference.residential_type && (
                  <div>
                    <p className="text-sm text-gray-500">Konut Tipi</p>
                    <p className="font-semibold">{getRelationName(preference.residential_type)}</p>
                  </div>
                )}
                {preference.room_definition && (
                  <div>
                    <p className="text-sm text-gray-500">Oda Sayısı</p>
                    <p className="font-semibold">{getRelationName(preference.room_definition)}</p>
                  </div>
                )}
                {preference.facade_type && (
                  <div>
                    <p className="text-sm text-gray-500">Cephe</p>
                    <p className="font-semibold">{getRelationName(preference.facade_type)}</p>
                  </div>
                )}
                {(preference.city || preference.district || preference.neighborhood) && (
                  <div>
                    <p className="text-sm text-gray-500">Konum</p>
                    <p className="font-semibold">
                      {preference.city && `${preference.city.name}`}
                      {preference.district && ` / ${preference.district.name}`}
                      {preference.neighborhood && ` / ${preference.neighborhood.name}`}
                    </p>
                  </div>
                )}
                {(preference.min_floor || preference.max_floor) && (
                  <div>
                    <p className="text-sm text-gray-500">Kat</p>
                    <p className="font-semibold">{preference.min_floor || '?'} - {preference.max_floor || '?'}</p>
                  </div>
                )}
                {preference.has_elevator && (
                  <div>
                    <p className="text-sm text-gray-500">Asansör</p>
                    <p className="font-semibold">Var</p>
                  </div>
                )}
                {(preference.min_sqm || preference.max_sqm) && (
                  <div>
                    <p className="text-sm text-gray-500">Metrekare</p>
                    <p className="font-semibold">{preference.min_sqm || '?'} - {preference.max_sqm || '?'} m²</p>
                  </div>
                )}
                {(preference.min_price || preference.max_price) && (
                  <div>
                    <p className="text-sm text-gray-500">Fiyat Aralığı</p>
                    <p className="font-semibold">
                      {preference.min_price?.toLocaleString('tr-TR') || '?'} - {preference.max_price?.toLocaleString('tr-TR') || '?'} ₺
                    </p>
                  </div>
                )}
                {preference.additional_notes && (
                  <div className="md:col-span-3">
                    <p className="text-sm text-gray-500">Ek Notlar</p>
                    <p className="font-semibold">{preference.additional_notes}</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 ml-4">
                <Button size="sm" onClick={() => navigate(buildPreferenceFilterURL(preference))}>
                  <Search size={16} />
                  Uygun İlanlar
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleEdit(preference)}>
                  Düzenle
                </Button>
                <Button size="sm" variant="danger" onClick={() => handleDelete(preference.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MeetingPreference;
